/**
 * ControlGrid — control cards above the finding list (Results v2 §5, build
 * step 3).
 *
 * TWO LEVELS ONLY: control → test. The catalog's family/bundle level is not
 * in any API response, so a third level would be an invented grouping — it
 * stays blueprint work. Every card is derived from the run's findings alone:
 * `controlCode` exists on every finding, so grouping is free and no new
 * contract is consumed.
 *
 * Sort: unanswered count descending, then `not_satisfied` count, then
 * `controlCode`. Weight-at-risk ordering needs per-test weights no response
 * serves — and serving raw ARC weights is an IP question before it is an API
 * one — so the top-left card earns its place by the honest metric available.
 *
 * The micro-bar is the §3 encoding at micro scale, resolved through the SAME
 * state matrix as the chips (`resolveFindingState` — never ad-hoc outcome
 * checks): fill presence = answered, texture = basis (the §10.1 prototype
 * verdict: the 2px/5px hatch survives at small heights with a 12px segment
 * min-width, so the fill-opacity fallback is unused), colour + a redundant
 * mark = outcome (§3.4 — `not_satisfied` ×, `marginal` ~, satisfied
 * unmarked; the three open treatments differ by border, not colour). Every
 * segment announces all three axes to a screen reader and is
 * keyboard-focusable; activating one opens its finding in the list below.
 *
 * Names (DC-1807): a card shows its control's catalog NAME when the run's
 * findings carry one — entitlement-joined server-side at read time, exactly
 * the `expects` rule, never a static string in this file. Entitlement gone →
 * `controlName` null → the identifier stands alone. The pager still counts
 * by identifier: the grouping level above control has no API response. No
 * percentage, score, or gauge appears at any scale: counts (and
 * entitlement-served names) are all this component is allowed.
 */

import React, { useMemo, useState } from 'react';
import type { RunFinding } from './assessmentTypes';
import { resolveFindingState, type FindingVisualState } from './findingState';

const CARDS_PER_PAGE = 12;

interface ControlCard {
  controlCode: string;
  /** Catalog name, entitlement-served on the findings (DC-1807); null = code alone. */
  controlName: string | null;
  /** In finding order — the run's own ordering, one segment per test. */
  tests: { finding: RunFinding; state: FindingVisualState }[];
  answered: number;
  unanswered: number;
  notSatisfied: number;
  measured: number;
}

function buildCards(findings: readonly RunFinding[]): ControlCard[] {
  const byControl = new Map<string, ControlCard>();
  for (const finding of findings) {
    let card = byControl.get(finding.controlCode);
    if (!card) {
      card = {
        controlCode: finding.controlCode,
        controlName: finding.controlName,
        tests: [],
        answered: 0,
        unanswered: 0,
        notSatisfied: 0,
        measured: 0,
      };
      byControl.set(finding.controlCode, card);
    }
    const state = resolveFindingState(finding);
    card.tests.push({ finding, state });
    if (state.kind === 'answered') {
      card.answered += 1;
      if (state.outcome === 'not_satisfied') card.notSatisfied += 1;
      if (state.basis === 'measured') card.measured += 1;
    } else {
      card.unanswered += 1;
    }
  }
  return [...byControl.values()].sort(
    (a, b) =>
      b.unanswered - a.unanswered ||
      b.notSatisfied - a.notSatisfied ||
      a.controlCode.localeCompare(b.controlCode)
  );
}

/**
 * The card's counts line — the redundant outcome channel at micro scale
 * (§3.4/§5). Counts only, never a percentage.
 */
function cardSummary(card: ControlCard): string {
  const parts: string[] = [];
  if (card.answered > 0) {
    parts.push(card.measured > 0 ? `${card.measured} measured` : 'attested only');
  }
  if (card.notSatisfied > 0) parts.push(`${card.notSatisfied} not satisfied`);
  return parts.join(' · ');
}

/** All three axes, announced — never a colour word (§3.4). The test's
 * catalog name rides along when served (DC-1807). */
function segmentLabel(finding: RunFinding, state: FindingVisualState): string {
  const outcome = finding.outcome.replace(/_/g, ' ');
  const basis = state.kind === 'answered' ? state.basis : 'no evidence';
  const name = finding.testName ? ` ${finding.testName}` : '';
  return `${finding.testCode}${name}, ${outcome}, ${basis}`;
}

function segmentClass(state: FindingVisualState): string {
  if (state.kind === 'answered') {
    return `uc-grid-seg uc-grid-seg-${state.tone} uc-grid-seg-${state.basis}`;
  }
  return `uc-grid-seg uc-grid-seg-open uc-grid-seg-${state.treatment}`;
}

/** §3.4 micro marks: not_satisfied ×, marginal ~, satisfied unmarked. */
function segmentMark(state: FindingVisualState): string | null {
  if (state.kind !== 'answered') return null;
  if (state.outcome === 'not_satisfied') return '×';
  if (state.outcome === 'marginal') return '~';
  return null;
}

export interface ControlGridProps {
  findings: readonly RunFinding[];
  /** The control the finding list is currently filtered to, or null. */
  activeControl: string | null;
  onToggleControl: (controlCode: string) => void;
  /** Activating a segment opens that finding in the list below. */
  onOpenFinding: (testCode: string) => void;
}

export const ControlGrid: React.FC<ControlGridProps> = ({
  findings,
  activeControl,
  onToggleControl,
  onOpenFinding,
}) => {
  const cards = useMemo(() => buildCards(findings), [findings]);
  const [page, setPage] = useState(0);

  if (cards.length === 0) return null;

  const pages = Math.ceil(cards.length / CARDS_PER_PAGE);
  const safePage = Math.min(page, pages - 1);
  const visible = cards.slice(safePage * CARDS_PER_PAGE, (safePage + 1) * CARDS_PER_PAGE);

  return (
    <div className="uc-grid" data-testid="control-grid">
      <ul className="uc-grid-cards">
        {visible.map((card) => (
          <li key={card.controlCode} className="uc-grid-card-item">
            <div
              className={
                activeControl === card.controlCode ? 'uc-grid-card is-active' : 'uc-grid-card'
              }
              data-testid={`grid-card-${card.controlCode}`}
            >
              <button
                type="button"
                className="uc-grid-card-head"
                aria-pressed={activeControl === card.controlCode}
                data-testid={`grid-card-toggle-${card.controlCode}`}
                onClick={() => onToggleControl(card.controlCode)}
              >
                <span className="uc-grid-card-code">{card.controlCode}</span>
                {card.controlName && (
                  <span
                    className="uc-grid-card-name"
                    data-testid={`grid-card-name-${card.controlCode}`}
                  >
                    {card.controlName}
                  </span>
                )}
                <span className="uc-grid-card-answered">
                  {card.answered} of {card.tests.length} answered
                </span>
                {cardSummary(card) && (
                  <span className="uc-grid-card-summary">{cardSummary(card)}</span>
                )}
              </button>
              <div className="uc-grid-bar" role="group" aria-label={`${card.controlCode} tests`}>
                {card.tests.map(({ finding, state }) => {
                  const mark = segmentMark(state);
                  return (
                    <button
                      key={finding.testCode}
                      type="button"
                      className={segmentClass(state)}
                      aria-label={segmentLabel(finding, state)}
                      data-testid={`grid-seg-${finding.testCode}`}
                      onClick={() => onOpenFinding(finding.testCode)}
                    >
                      {mark && (
                        <span className="uc-grid-mark" aria-hidden="true">
                          {mark}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {activeControl !== null && (
        <p className="uc-grid-filter-note" data-testid="grid-filter-note">
          Findings below are filtered to {activeControl}.{' '}
          <button
            type="button"
            className="uc-grid-filter-clear"
            data-testid="grid-filter-clear"
            onClick={() => onToggleControl(activeControl)}
          >
            Show all controls
          </button>
        </p>
      )}

      {pages > 1 && (
        <div className="uc-grid-pager" data-testid="grid-pager">
          <button
            type="button"
            className="banking-btn banking-btn-secondary"
            disabled={safePage === 0}
            onClick={() => setPage(safePage - 1)}
          >
            Previous
          </button>
          {/* Identifiers only — no family names exist in any response. */}
          <span className="uc-grid-pager-note">
            controls {safePage * CARDS_PER_PAGE + 1}–
            {Math.min((safePage + 1) * CARDS_PER_PAGE, cards.length)} of {cards.length}
          </span>
          <button
            type="button"
            className="banking-btn banking-btn-secondary"
            disabled={safePage >= pages - 1}
            onClick={() => setPage(safePage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ControlGrid;
